import React, { useState, useContext, useEffect, useMemo, useCallback } from 'react';
import { withRouter } from "react-router-dom";
import { AppActions } from 'store';
import { DispatchContext } from 'context';
import { Api } from 'services';
import './inicio.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapMarkerAlt, faPhone, faPhoneAlt } from '@fortawesome/free-solid-svg-icons';
import { faFacebookSquare, faInstagram, faWhatsapp } from '@fortawesome/free-brands-svg-icons';

import { CarouselProvider, Slider, Slide, Dot } from 'pure-react-carousel';
import 'pure-react-carousel/dist/react-carousel.es.css';

import { useWindowSize } from 'hooks';
import { NoImageAvailable, Slide3 } from 'assets/img';

const Inicio = React.memo(props => {
    const dispatch = useContext(DispatchContext);
    // const state = useContext(StateContext);
    const windowSize = useWindowSize();
    const [slides, setSlides] = useState([]);
    const [isCarouselVisible, setIsCarouselVisible] = useState(false);
    const [loading, setLoading] = useState(true);

    const containerHeight = useMemo(() => {
        return windowSize.width <= 600 ? windowSize.height - 100 : windowSize.height;
    }, [windowSize.height, windowSize.width]);

    // Cargar imágenes de portada desde la API
    const cargarPortadas = useCallback(async () => {
       
            setLoading(true);
            const options = { 
                url: Api.route('/portada_imagen/listar'), 
                method: 'GET'
            };

            Api.request(options, response => {
                if (response.codigo === 200 && response.datos?.data) {
                    // Filtrar solo las portadas activas
                    const portadasActivas = response.datos.data.filter(p => p.port_activo === 1);
                    
                    // Si no hay portadas activas, cargar imagen predeterminada
                    if (portadasActivas.length === 0) {
                        const slidesPredeterminados = [{
                            id: 'default-1',
                            img: Slide3,
                            letter: 'R',
                            destination: 'Rio de Janeiro',
                            alt: 'Servitravel - Agencia de Viajes'
                        }];
                        setSlides(slidesPredeterminados);
                    } else {
                        // Convertir el formato de la API al formato del carousel
                        const slidesData = portadasActivas.map((portada, index) => ({
                            id: portada.id_portada,
                            img: portada.port_url ? Api.route("/" + portada.port_url) : NoImageAvailable,
                            letter: portada.port_titulo ? portada.port_titulo.charAt(0).toUpperCase() : '',
                            destination: portada.port_titulo || 'Sin título',
                            alt: `${portada.port_titulo || 'Destino'} - Servitravel`
                        }));
                        
                        setSlides(slidesData);
                    }
                } else {
                    // Si la respuesta no es exitosa, cargar imagen predeterminada
                    const slidesPredeterminados = [{
                        id: 'default-1',
                        img: Slide3,
                        letter: 'R',
                        destination: 'Rio de Janeiro',
                        alt: 'Servitravel - Agencia de Viajes'
                    }];
                    setSlides(slidesPredeterminados);
                }
                setLoading(false);
            });
    }, []);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setIsCarouselVisible(true);
                        observer.disconnect();
                    }
                });
            },
            { threshold: 0.1 }
        );

        const carouselContainer = document.getElementById('carousel-container');
        if (carouselContainer) {
            observer.observe(carouselContainer);
        }

        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        requestAnimationFrame(() => {
            dispatch(AppActions.setLoading(false));
            dispatch(AppActions.setActionsDisabled(false));
        });
        
        document.title = 'Servitravel.com.py - Agencia de Viajes en Paraguay';
        
        // Cargar portadas desde la API
        cargarPortadas();
    }, [dispatch, cargarPortadas]);

    const renderCarousel = useCallback(() => {
        if (loading || !isCarouselVisible) {
            return (
                <div 
                    id="carousel-container"
                    style={{ 
                        width: '100%', 
                        height: containerHeight + 'px', 
                        position: 'relative', 
                        margin: 'auto',
                        backgroundColor: '#2c3e50'
                    }}
                    aria-label="Cargando carousel de promociones"
                >
                    <div style={{
                        width: '100%',
                        height: '100%',
                        background: 'linear-gradient(90deg, #2c3e50 25%, #34495e 50%, #2c3e50 75%)',
                        backgroundSize: '200% 100%',
                        animation: 'loading 1.5s infinite'
                    }}></div>
                </div>
            );
        }

        return (
            <div id="carousel-container" style={{ position: 'relative', width: '100%', height: containerHeight + 'px' }}>
                <CarouselProvider
                    naturalSlideWidth={100}
                    naturalSlideHeight={60}
                    totalSlides={slides.length}
                    visibleSlides={1}
                    infinite={slides.length > 1}
                    interval={5000}
                    isPlaying={slides.length > 1}
                    role="region"
                    aria-label="Destinos de Servitravel"
                >
                    <Slider style={{ height: containerHeight + 'px' }} role="group" aria-roledescription="carousel">
                        {slides.map((slide, index) => (
                            <Slide 
                                key={slide.id} 
                                index={index}
                                aria-label={`Slide de ${slide.destination}`}
                            >
                                <div 
                                    style={{ 
                                        width: '100%', 
                                        height: containerHeight + 'px',
                                        backgroundImage: `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url('${slide.img}')`,
                                        backgroundSize: 'cover',
                                        backgroundPosition: 'center',
                                        backgroundRepeat: 'no-repeat',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexDirection: 'column',
                                        position: 'relative'
                                    }}
                                    role="img"
                                    aria-label={slide.alt}
                                >
                                    {/* Contenedor del texto centrado */}
                                    <div style={{
                                        textAlign: 'center',
                                        animation: 'fadeInUp 1s ease-out',
                                        position: 'relative',
                                        minHeight: '300px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }} >
                                        {/* Letra grande de fondo */}
                                        {slide.letter && (
                                            <h3 style={{
                                                fontSize: 'clamp(15rem, 25vw, 35rem)',
                                                fontWeight: 'bold',
                                                margin: '0',
                                                lineHeight: '0.8',
                                                letterSpacing: '0.05em',
                                                WebkitTextFillColor: 'transparent',
                                                MozTextFillColor: 'transparent',
                                                textFillColor: 'transparent',
                                                background: `url('${slide.img}')`,
                                                backgroundSize: 'cover',
                                                backgroundPosition: 'center center',
                                                backgroundRepeat: 'no-repeat',
                                                backgroundAttachment: 'fixed',
                                                WebkitBackgroundClip: 'text',
                                                MozBackgroundClip: 'text',
                                                backgroundClip: 'text',
                                                color: 'rgba(255, 255, 255, 0.1)',
                                                filter: 'contrast(1.2) brightness(1.1)',
                                                position: 'absolute',
                                                top: '50%',
                                                left: '50%',
                                                transform: 'translate(-50%, -50%)',
                                                width: '100%',
                                                zIndex: 1
                                            }}>
                                                {slide.letter}
                                            </h3>
                                        )}
                                        
                                        {/* Nombre del destino */}
                                        <h4 style={{
                                            color: 'white',
                                            fontSize: 'clamp(2.5rem, 6vw, 5rem)',
                                            fontWeight: '600',
                                            margin: '0',
                                            textShadow: '3px 3px 10px rgba(0,0,0,0.9)',
                                            letterSpacing: '0.15em',
                                            textTransform: 'uppercase',
                                            position: 'relative',
                                            zIndex: 2
                                        }}>
                                            {slide.destination}
                                        </h4>
                                    </div>
                                    
                                    {/* Imagen oculta para SEO */}
                                    <img 
                                        src={slide.img} 
                                        alt={slide.alt}
                                        style={{ 
                                            position: 'absolute', 
                                            width: '1px', 
                                            height: '1px', 
                                            opacity: 0,
                                            pointerEvents: 'none'
                                        }}
                                        loading={index === 0 ? "eager" : "lazy"}
                                        decoding="async"
                                    />
                                </div>
                            </Slide>
                        ))}
                    </Slider>
                    
                    {/* Indicadores de puntos (dots) - solo si hay más de 1 slide */}
                    {slides.length > 1 && (
                        <div style={{
                            position: 'absolute',
                            bottom: windowSize.width <= 600 ? '160px' : '120px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            display: 'flex',
                            gap: '12px',
                            zIndex: 10
                        }}
                        >
                            {slides.map((_, index) => (
                                <Dot 
                                    key={index}
                                    slide={index}
                                    style={{
                                        width: '14px',
                                        height: '14px',
                                        borderRadius: '50%',
                                        border: '2px solid white',
                                        cursor: 'pointer',
                                        transition: 'all 0.3s ease',
                                        padding: 0
                                    }}
                                    className="carousel-dot"
                                />
                            ))}
                        </div>
                    )}
                </CarouselProvider>

                {/* Estilos CSS adicionales */}
                <style>{`
                    @keyframes fadeInUp {
                        from {
                            opacity: 0;
                            transform: translateY(40px);
                        }
                        to {
                            opacity: 1;
                            transform: translateY(0);
                        }
                    }
                    
                    @keyframes loading {
                        0% { background-position: 200% 0; }
                        100% { background-position: -200% 0; }
                    }
                    
                    .carousel-dot:hover {
                        transform: scale(1.2);
                    }
                    
                    .carousel__dot--selected {
                        background-color: white !important;
                    }
                `}</style>
            </div>
        );
    }, [slides, isCarouselVisible, containerHeight, windowSize.width, loading]);

    return (
        <main id="inicio-container" className="bs-bbox" role="main">
            <div 
                className="bs-bbox" 
                style={{ 
                    width: '100%', 
                    height: '100%', 
                    position: 'relative'
                }}
            >
                {renderCarousel()}
                
                <footer 
                    style={{ 
                        width: '100%', 
                        position: 'absolute', 
                        bottom: 0, 
                        left: 0, 
                        backgroundColor: 'rgba(0,0,0,0.3)',
                        zIndex: 1,
                        padding: '15px 0'
                    }}
                    role="contentinfo"
                >
                    <div className="w3-row">
                        <div className="w3-col l8 m7 bs-bbox sucursales" style={{ padding: '10px 20px' }}>
                            <a 
                                className="w3-text-white xbold" 
                                href="https://goo.gl/maps/18ULYUt3AJn37wUA7" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                aria-label="Ubicación de Casa Central - Abrir en Google Maps"
                                style={{ textDecoration: 'none', //display: 'inline-flex' //, alignItems: 'center' 

                                }}
                            >
                                <FontAwesomeIcon 
                                    icon={faMapMarkerAlt} 
                                    color="#fff" 
                                    size="1x"
                                    aria-hidden="true"
                                />
                                &nbsp;&nbsp; Casa Central: &nbsp;  <FontAwesomeIcon 
                                    icon={faPhoneAlt} 
                                    color="#fff" 
                                    size="1x"
                                    aria-hidden="true"
                                /> +595 (021) 210 363 &nbsp;  <FontAwesomeIcon 
                                    icon={faWhatsapp} 
                                    color="#fff" 
                                    size="1x"
                                    aria-hidden="true"
                                /> +595 986 229 400 &nbsp; - &nbsp; Brasil 1050 e/ Tte. Fariña y Rca. de Colombia, Paraguay
                            </a>
                        </div>
                        <nav 
                            id="social-links-container" 
                            className="w3-col l4 m5 w3-padding bs-bbox"
                            aria-label="Enlaces de redes sociales"
                            style={{ display: 'flex', gap: '15px', justifyContent: 'center', alignItems: 'center' }}
                        >
                            <a 
                                href="https://www.facebook.com/ServiTravel" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                aria-label="Seguir en Facebook"
                                style={{ transition: 'opacity 0.3s' }}
                                onMouseOver={(e) => e.currentTarget.style.opacity = '0.7'}
                                onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
                            >
                                <FontAwesomeIcon 
                                    icon={faFacebookSquare} 
                                    color="#fff" 
                                    size="2x" 
                                    aria-hidden="true" 
                                />
                            </a>
                            <a 
                                href="https://www.instagram.com/servitravel/" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                aria-label="Seguir en Instagram"
                                style={{ transition: 'opacity 0.3s' }}
                                onMouseOver={(e) => e.currentTarget.style.opacity = '0.7'}
                                onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
                            >
                                <FontAwesomeIcon 
                                    icon={faInstagram} 
                                    color="#fff" 
                                    size="2x" 
                                    aria-hidden="true" 
                                />
                            </a>
                            <a 
                                href="https://wa.me/595986229400" 
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Contactar por WhatsApp"
                                style={{ transition: 'opacity 0.3s' }}
                                onMouseOver={(e) => e.currentTarget.style.opacity = '0.7'}
                                onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
                            >
                                <FontAwesomeIcon 
                                    icon={faWhatsapp} 
                                    color="#fff" 
                                    size="2x" 
                                    aria-hidden="true" 
                                />
                            </a>
                        </nav>
                    </div>
                </footer>
            </div>
        </main>
    );
});

Inicio.displayName = 'Inicio';

export default withRouter(Inicio);